import { Link, useRouter } from '@tanstack/react-router';
import type { ReactNode } from 'react';

/**
 * "Back" that actually goes back.
 *
 * Every detail page used to hard-code its return path ("← Back to journal"), so
 * arriving at a journal entry from the General Ledger, Books Health or a bank
 * match and pressing Back dumped you on the journal list — a page you had never
 * been on. That is the single most common navigation complaint in the app.
 *
 * So: pop real browser history when there is history to pop, and fall back to
 * the explicit `to` only when there isn't (fresh tab, pasted link, first page
 * of the session). The fallback then renders as a normal Link so it still
 * middle-clicks and shows a real href.
 */
export function BackLink({
  to,
  children,
  style,
}: {
  /** Where to go when there is no history to pop (direct load, new tab). */
  to: string;
  children?: ReactNode;
  style?: React.CSSProperties;
}) {
  const router = useRouter();
  const canGoBack = router.history.canGoBack();

  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 13,
    ...style,
  };

  if (!canGoBack) {
    return (
      <Link to={to} style={baseStyle}>
        ← {children ?? 'Back'}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={() => router.history.back()}
      className="link-button"
      style={baseStyle}
    >
      ← {children ?? 'Back'}
    </button>
  );
}
